import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { api } from "@/api/client";
import { useToast } from "@/app/toast";

import { MkAvatarEditor } from "./MkAvatarEditor";
import { MkButton } from "./MkButton";
import { MkChoice } from "./MkChoice";
import { MkColorInput } from "./MkColorInput";
import { MkInput } from "./MkInput";
import { MkMany2One } from "./MkMany2One";
import { MkModal } from "./MkModal";
import { MkSelect } from "./MkSelect";
import { MkSkeleton } from "./MkSkeleton";
import type { RelRecord } from "./useRelSearch";

interface Meta {
    label: string;
    options: { id: string; label: string }[];
}

interface Settings {
    id: number;
    values: Record<string, string | number | false | RelRecord | null>;
    has_logo: boolean;
    has_background: boolean;
    has_stamp: boolean;
    meta: Record<string, Meta>;
}

const THEME_COLORS = [
    { name: "mk_primary_color", fallback: "#3b82f6" },
    { name: "mk_secondary_color", fallback: "#083344" },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type Values = Record<string, string | number | false | RelRecord | null>;

export function MkCompanyModal({ open, onClose }: { open: boolean; onClose(): void }) {
    const { t } = useTranslation();
    const toast = useToast();
    const [record, setRecord] = useState<Values | null>(null);
    const [errors, setErrors] = useState<{ name?: string; email?: string }>({});
    const [saving, setSaving] = useState(false);
    const [themeTouched, setThemeTouched] = useState(false);
    const [images, setImages] = useState({
        logo: { value: false as string | false, has: false, touched: false },
        background: { value: false as string | false, has: false, touched: false },
        stamp: { value: false as string | false, has: false, touched: false },
    });

    const settings = useQuery<{ data: Settings }>({
        queryKey: ["me", "company-settings"],
        queryFn: () => api.raw<Settings>("/me/company-settings"),
        enabled: open,
        staleTime: 0,
    });

    const data = settings.data?.data;

    useEffect(() => {
        if (settings.isError) {
            toast.danger(t("mk.company.loadFailed"));
        }
    }, [settings.isError, toast, t]);

    useEffect(() => {
        if (!data || record) {
            return;
        }
        setRecord({ ...data.values });
        setImages({
            logo: { value: false, has: data.has_logo, touched: false },
            background: { value: false, has: data.has_background, touched: false },
            stamp: { value: false, has: data.has_stamp, touched: false },
        });
    }, [data, record]);

    const meta = data?.meta ?? {};
    const label = (name: string) => meta[name]?.label || name;
    const choices = (name: string) => (meta[name]?.options ?? []);

    function set(key: string, value: Values[string]) {
        setRecord((current) => (current ? { ...current, [key]: value } : current));
        if (key === "name" || key === "email") {
            setErrors((current) => ({ ...current, [key]: "" }));
        }
    }

    function setTheme(key: string, value: Values[string]) {
        set(key, value);
        setThemeTouched(true);
    }

    function setImage(kind: "logo" | "background" | "stamp", value: string | false) {
        setImages((current) => ({ ...current, [kind]: { value, has: Boolean(value), touched: true } }));
    }

    async function onLogoUpload(dataUrl: string) {
        setImage("logo", dataUrl);
        const payload = String(dataUrl || "").split(",")[1] || "";
        if (!payload) {
            return;
        }
        try {
            const colors = await api.post<{ primary: string | false; secondary: string | false }>(
                "/me/company-settings/logo-colors", { image: payload });
            if (colors.data.primary) {
                setTheme("mk_primary_color", colors.data.primary);
                setTheme("mk_secondary_color", colors.data.secondary || false);
            }
        } catch {
            return;
        }
    }

    async function save() {
        if (!record || !data) {
            return;
        }
        const name = String(record.name || "");
        const email = String(record.email || "");
        const next = {
            name: name.trim() ? "" : t("mk.validation.required"),
            email: email && !EMAIL_RE.test(email) ? t("mk.validation.email") : "",
        };
        setErrors(next);
        if (next.name || next.email) {
            return;
        }
        const values: Record<string, unknown> = {};
        for (const key of ["name", "email", "phone", "website", "vat", "company_registry",
                           "street", "street2", "city", "zip"]) {
            values[key] = record[key] || false;
        }
        const country = record.country_id as RelRecord | null | false;
        values.country_id = (country && country.id) || false;
        for (const key of ["mk_primary_color", "mk_secondary_color", "mk_default_theme",
                           "mk_font_family", "mk_font_size", "mk_sidebar_mode"]) {
            values[key] = record[key] || false;
        }
        values.mk_page_size = Number.parseInt(String(record.mk_page_size ?? ""), 10) || 30;
        if (images.logo.touched) {
            values.logo = images.logo.value ? String(images.logo.value).split(",")[1] || false : false;
        }
        if (images.background.touched) {
            values.mk_owl_bg_image = images.background.value
                ? String(images.background.value).split(",")[1] || false : false;
        }
        if (images.stamp.touched) {
            values.mk_stamp_image = images.stamp.value
                ? String(images.stamp.value).split(",")[1] || false : false;
        }
        setSaving(true);
        try {
            await api.patch("/me/company-settings", values);
            if (images.logo.touched || images.background.touched || themeTouched) {
                window.location.reload();
                return;
            }
            toast.success(t("mk.company.updated"));
            onClose();
        } catch {
            toast.danger(t("mk.company.saveFailed"));
        } finally {
            setSaving(false);
        }
    }

    const companyId = data?.id;
    const imageUrl = (field: string) => `/api/v1/files/image/res.company/${companyId}/${field}`;
    const subtitle = String(record?.email || record?.website || "");

    return (
        <MkModal open={open} size="md" onClose={onClose} title={t("mk.company.title")}
                 subtitle={subtitle} icon="fa-building"
                 footer={(
                     <>
                         <MkButton variant="ghost" label={t("mk.cancel")} onClick={onClose} />
                         <MkButton variant="primary" label={t("mk.saveChanges")} icon="fa-check"
                                   loading={saving} onClick={() => void save()} />
                     </>
                 )}>
            <div className="mk-company-modal">
                {!record || !data ? (
                    <>
                        <div className="mk-company-head">
                            <MkSkeleton variant="circle" width="84px" height="84px" />
                            <div className="mk-flex-1">
                                <MkSkeleton width="60%" />
                                <MkSkeleton width="40%" />
                            </div>
                        </div>
                        <MkSkeleton lines={4} />
                    </>
                ) : (
                    <>
                        <div className="mk-company-head">
                            <MkAvatarEditor size="xl" square src={imageUrl("logo")}
                                            value={images.logo.value} hasImage={images.logo.has}
                                            name={String(record.name || "")}
                                            title={t("mk.company.logo")}
                                            onUpload={(url) => void onLogoUpload(url)}
                                            onClear={() => setImage("logo", false)} />
                            <div className="mk-flex-1">
                                <div className="mk-company-name">{String(record.name || "")}</div>
                                <div className="mk-company-mail">{String(record.email || "")}</div>
                            </div>
                        </div>

                        <div className="mk-form-grid">
                            <MkInput label={t("mk.company.name")} value={String(record.name || "")}
                                     required error={errors.name}
                                     onChange={(value) => set("name", value)} />
                            <MkInput label={t("mk.company.email")} type="email"
                                     value={String(record.email || "")} error={errors.email}
                                     onChange={(value) => set("email", value)} />
                            <MkInput label={t("mk.company.phone")} value={String(record.phone || "")}
                                     onChange={(value) => set("phone", value)} />
                            <MkInput label={t("mk.company.website")}
                                     value={String(record.website || "")}
                                     onChange={(value) => set("website", value)} />
                            <MkInput label={t("mk.company.vat")} value={String(record.vat || "")}
                                     onChange={(value) => set("vat", value)} />
                            <MkInput label={t("mk.company.registry")}
                                     value={String(record.company_registry || "")}
                                     onChange={(value) => set("company_registry", value)} />
                            <MkInput label={t("mk.company.street")} value={String(record.street || "")}
                                     onChange={(value) => set("street", value)} />
                            <MkInput label={t("mk.company.street2")}
                                     value={String(record.street2 || "")}
                                     onChange={(value) => set("street2", value)} />
                            <MkInput label={t("mk.company.city")} value={String(record.city || "")}
                                     onChange={(value) => set("city", value)} />
                            <MkInput label={t("mk.company.zip")} value={String(record.zip || "")}
                                     onChange={(value) => set("zip", value)} />
                            <MkMany2One label={t("mk.company.country")}
                                        value={record.country_id as RelRecord | null}
                                        canCreate={false} cacheKey="res.country"
                                        search={(query, limit) => api.raw<RelRecord[]>(
                                            `/me/company-settings/countries?q=${encodeURIComponent(query)}&limit=${limit}`)
                                            .then((payload) => payload.data)}
                                        onUpdate={(value) => set("country_id", value || null)} />
                        </div>

                        <div className="mk-company-section">
                            <div className="mk-company-section-title">
                                <i className="fa fa-paint-brush" />
                                <span>{t("mk.company.brandColors")}</span>
                            </div>
                            <div className="mk-color-grid">
                                {THEME_COLORS.map((color) => (
                                    <MkColorInput key={color.name} label={label(color.name)}
                                                  value={(record[color.name] as string | false) || ""}
                                                  fallback={color.fallback}
                                                  onUpdate={(value) => setTheme(color.name, value)} />
                                ))}
                            </div>
                        </div>

                        <div className="mk-company-section">
                            <div className="mk-company-section-title">
                                <i className="fa fa-certificate" />
                                <span>{t("mk.company.stamp")}</span>
                            </div>
                            <div className="mk-company-bg">
                                <MkAvatarEditor size="xl" square src={imageUrl("mk_stamp_image")}
                                                value={images.stamp.value} hasImage={images.stamp.has}
                                                name="STAMP" title={t("mk.company.stamp")}
                                                onUpload={(url) => setImage("stamp", url)}
                                                onClear={() => setImage("stamp", false)} />
                                <div className="mk-company-bg-text">
                                    <div className="mk-field-label">{t("mk.company.stamp")}</div>
                                    <div className="mk-company-bg-hint">{t("mk.company.stampHint")}</div>
                                </div>
                            </div>
                        </div>

                        <div className="mk-company-section">
                            <div className="mk-company-section-title">
                                <i className="fa fa-magic" />
                                <span>{t("mk.company.appearance")}</span>
                            </div>
                            <div className="mk-company-prefs">
                                <MkChoice label={label("mk_default_theme")}
                                          options={choices("mk_default_theme")}
                                          value={String(record.mk_default_theme || "")}
                                          onChange={(value) => setTheme("mk_default_theme", value)} />
                                <MkChoice label={label("mk_sidebar_mode")}
                                          options={choices("mk_sidebar_mode")}
                                          value={String(record.mk_sidebar_mode || "")}
                                          onChange={(value) => setTheme("mk_sidebar_mode", value)} />
                                <MkChoice label={label("mk_font_size")} wrap
                                          options={choices("mk_font_size")}
                                          value={String(record.mk_font_size || "")}
                                          onChange={(value) => setTheme("mk_font_size", value)} />
                                <div className="mk-form-grid">
                                    <MkSelect label={label("mk_font_family")}
                                              options={choices("mk_font_family").map((option) => ({
                                                  value: option.id, label: option.label }))}
                                              value={String(record.mk_font_family || "")}
                                              onChange={(value) => setTheme("mk_font_family", value)} />
                                    <MkInput type="number" label={label("mk_page_size")}
                                             value={String(record.mk_page_size ?? "")}
                                             onChange={(value) => setTheme("mk_page_size", value)} />
                                </div>
                                <div className="mk-company-bg">
                                    <MkAvatarEditor size="xl" square src={imageUrl("mk_owl_bg_image")}
                                                    value={images.background.value}
                                                    hasImage={images.background.has}
                                                    name="BG" title={t("mk.company.background")}
                                                    onUpload={(url) => setImage("background", url)}
                                                    onClear={() => setImage("background", false)} />
                                    <div className="mk-company-bg-text">
                                        <div className="mk-field-label">{t("mk.company.background")}</div>
                                        <div className="mk-company-bg-hint">{t("mk.company.backgroundHint")}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </MkModal>
    );
}
